import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUpdateVariable, useVariable } from "../../hooks/useVariables";
import { VariableUpdate } from "../../types/variable";
import AuditInfo from "../../components/ui/AuditInfo";
import { auditApi } from "../../services/api";
import { Input, Textarea } from "../../components/ui/Input";

const EditVariablePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const variableId = Number(id);
  const navigate = useNavigate();
  const { data: v, isLoading } = useVariable(variableId);
  const updateMutation = useUpdateVariable();
  const [form, setForm] = useState<VariableUpdate>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (v) {
      setForm({
        description: v.description || "",
        sql_query: v.sql_query || "",
        db_name: v.db_name || "",
        transform: v.transform || "",
        cache_expiration_minutes: v.cache_expiration_minutes ?? undefined,
        not_expire: v.not_expire,
        http_request_endpoint_id: v.http_request_endpoint_id ?? undefined,
        endpoint_field: v.endpoint_field || "",
      });
    }
  }, [v]);

  const handleChange = (field: keyof VariableUpdate, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: VariableUpdate = {
        description: form.description || undefined,
        sql_query: form.sql_query || undefined,
        db_name: form.db_name || undefined,
        transform: form.transform || undefined,
        cache_expiration_minutes: form.cache_expiration_minutes ?? undefined,
        not_expire: form.not_expire,
        http_request_endpoint_id: form.http_request_endpoint_id ?? undefined,
        endpoint_field: form.endpoint_field || undefined,
      };
      const updated = await updateMutation.mutateAsync({
        id: variableId,
        data: payload,
      });
      navigate(`/variables/${updated.id}`);
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !v) {
    return <div className="py-12 text-center">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Edit Variable: {v.var_name}
        </h2>
        <p className="text-sm text-gray-500">Update variable configuration</p>
      </div>

      <form
        className="bg-white shadow sm:rounded-lg p-6 space-y-4"
        onSubmit={handleSubmit}
      >
        <Textarea
          label="Description"
          rows={3}
          value={form.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          variant="rounded-md"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="DB Name"
            type="text"
            value={form.db_name || ""}
            onChange={(e) => handleChange("db_name", e.target.value)}
            variant="rounded-md"
          />
          <Input
            label="Transform"
            type="text"
            value={form.transform || ""}
            onChange={(e) => handleChange("transform", e.target.value)}
            placeholder="pgp_decrypt, aes256_decrypt"
            variant="rounded-md"
          />
        </div>
        <Textarea
          label="SQL Query"
          rows={6}
          value={form.sql_query || ""}
          onChange={(e) => handleChange("sql_query", e.target.value)}
          placeholder="SELECT ... WHERE member_id = :member_id"
          variant="rounded-md"
          className="font-mono"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Cache Expiration (minutes)"
            type="number"
            min={0}
            value={form.cache_expiration_minutes ?? ""}
            onChange={(e) =>
              handleChange(
                "cache_expiration_minutes",
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
            variant="rounded-md"
          />
          <div className="flex items-center mt-6">
            <input
              id="not_expire"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
              checked={!!form.not_expire}
              onChange={(e) => handleChange("not_expire", e.target.checked)}
            />
            <label
              htmlFor="not_expire"
              className="ml-2 block text-sm text-gray-700"
            >
              Not expire (cache indefinitely)
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="HTTP Endpoint ID"
            type="number"
            value={form.http_request_endpoint_id ?? ""}
            onChange={(e) =>
              handleChange(
                "http_request_endpoint_id",
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
            variant="rounded-md"
          />
          <Input
            label="Endpoint Field"
            type="text"
            value={form.endpoint_field || ""}
            onChange={(e) => handleChange("endpoint_field", e.target.value)}
            placeholder="user.name or data[0].id"
            variant="rounded-md"
          />
        </div>

        <div className="flex justify-end space-x-2">
          <button
            type="button"
            className="btn-secondary px-4 py-2 rounded-md"
            onClick={() => navigate(`/variables/${variableId}`)}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary px-4 py-2 rounded-md"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
      {v?.id ? (
        <AuditInfo
          resource="variables"
          id={v.id}
          fetch={auditApi.getLastChange}
        />
      ) : null}
    </div>
  );
};

export default EditVariablePage;
